'use client'

import { alert } from '@repo/ui/Alert'
import { MoreVertIcon } from '@repo/ui/MoreVertIcon'
import { NavigationMenuItem } from '@repo/ui/NavigationMenuItem'
import { PopupWithBackdrop } from '@repo/ui/PopupWithBackdrop'
import { useClientTableStore } from '@repo/store/useClientTableStore'
import { useDataGridFilterStore } from '@repo/store/useDataGridFilterStore'
import { useEffect, useRef, useState } from 'react'
import { useKeyDateStore } from '@repo/store/useKeyDateStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useMatterTableStore } from '@repo/store/useMatterTableStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useMenuState, MenuItem, SubMenu, MenuDivider } from '@szhsin/react-menu'
import { useRouteLoaderData } from 'react-router-dom'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import { useTimeRecordingStore } from '@repo/store/useTimeRecordingStore'
import { useUndertakingsStore } from '@repo/store/useUndertakingsStore'
import { useUrlStore } from '@repo/store/useUrlStore'
import ClientMoreMenuButtonItems from './ClientMoreMenuButtonItems.jsx'
import IconButton from '@mui/material/IconButton'
import MatterMoreMenuButtonItems from './MatterMoreMenuButtonItems.jsx'
import MUIStack from '@mui/material/Stack'

export const MoreMenuCard = ({ isClientsTable, rowData, matterNo, clientNo, disabled }: MoreMenuCardProps) => {
  const [isDisabled, setIsDisabled] = useState(false)
  const [matterHasOutstandingUndertakings, setMatterHasOutstandingUndertakings] = useState(false)
  const [menuItems, setMenuItems] = useState([])
  const [menuState, toggleMenu] = useMenuState({ transition: true })
  const [undertakingsDischargeCount, setUndertakingsDischargeCount] = useState(0)
  const [undertakingsTotalCount, setUndertakingsTotalCount] = useState(0)

  const { toggleKeyDatesDrawer } = useKeyDateStore()
  const { allowedMenuIds } = useMenuItemsStore()
  const { clientsTableFilters, mattersTableFilters } = useDataGridFilterStore()
  const { clientTableDataCSV, fetchClientTableAsCSV } = useClientTableStore()
  const { currentClientMatterNo } = useMattersStore()
  const { matterTableDataCSV, fetchMatterTableAsCSV } = useMatterTableStore()
  const { fetchMatterUndertakingCount, fetchUndertakingExists, isUndertakingsSupported, undertakingsCounts } =
    useUndertakingsStore()

  const { setPostLoadAction, iframeSrc, routeId, navigateWithClientMatterNo } = useUrlStore()
  const { setTimeRecordingDrawerIsOpen, setTimeRecordingFormToDisplay } = useTimeRecordingStore()
  const ref = useRef(null)
  const loaderData = useRouteLoaderData(routeId)

  useEffect(() => {
    if (matterNo && undertakingsCounts && menuState.state === 'open') {
      const matchingCount = undertakingsCounts.find(
        (count: any) => count.clientNo === clientNo && count.matterNo === matterNo
      )

      if (matchingCount) {
        setMatterHasOutstandingUndertakings(matchingCount.matterHasOutstandingUndertakings)
        setUndertakingsDischargeCount(matchingCount.dischargedCount || 0)
        setUndertakingsTotalCount(matchingCount.totalCount || 0)
      } else {
        setMatterHasOutstandingUndertakings(false)
        setUndertakingsDischargeCount(0)
        setUndertakingsTotalCount(0)
      }
    }
  }, [undertakingsCounts, clientNo, matterNo, menuState])

  useEffect(() => {
    if (isClientsTable) {
      setMenuItems(ClientMoreMenuButtonItems)
    } else {
      // @ts-ignore

      const undertakingsExist = async () => {
        const exists = await fetchUndertakingExists({
          clientNo: clientNo,
          matterNo: matterNo
        })

        if (exists === true) {
          fetchMatterUndertakingCount({
            clientNo: clientNo,
            matterNo: matterNo
          })
        }
      }

      if (isUndertakingsSupported && menuState.state === 'open') {
        undertakingsExist()
      }

      const matterIsArchived = Boolean(rowData?.dateArchived)

      const menuItems = MatterMoreMenuButtonItems.filter((item: any) => {
        if (item.label === 'Archive matter') {
          return !matterIsArchived
        } else if (item.label === 'Restore matter') {
          return matterIsArchived
        }

        return true
      })

      setMenuItems(menuItems)
    }
  }, [
    clientNo,
    fetchMatterUndertakingCount,
    isClientsTable,
    isUndertakingsSupported,
    matterHasOutstandingUndertakings,
    menuState.state,
    matterNo,
    rowData,
    undertakingsDischargeCount,
    undertakingsTotalCount
  ])

  useEffect(() => {
    if (isClientsTable) {
      setIsDisabled(false)
      return
    }

    const matterNoIsValid = matterNo || matterNo === 0
    if (!clientNo || clientNo.trim() === '' || !matterNoIsValid || disabled) {
      setIsDisabled(true)
    } else {
      setIsDisabled(false)
    }
  }, [clientNo, matterNo, disabled, isClientsTable])

  const handleMenuItemClick = async (
    target: any,
    label: any,
    postLoadAction: any,
    postLoadSelector: string,
    appendClientMatterNoToTarget: boolean = false
  ) => {
    let shouldNavigate = target !== routeId

    if (postLoadAction) {
      if (!postLoadSelector) {
        postLoadSelector = postLoadAction === 'sendEmail' ? '#pra-send-email-client' : '#pra-send-sms'
      }

      let postLoadPage = 'matterpage.aspx'
      let postLoadActionShouldWaitForIframeToLoad = shouldNavigate || !iframeSrc.toLowerCase().includes(postLoadPage)

      const iframe = document.querySelector('iframe[name="ospreyIframe"]')
      // @ts-ignore
      const { contentWindow } = iframe || {}
      const element = contentWindow?.document?.querySelector(postLoadSelector)
      // @ts-ignore
      if (element && loaderData && loaderData.iframeVisible !== false) {
        shouldNavigate = false
        postLoadActionShouldWaitForIframeToLoad = false
      }

      setPostLoadAction({ postLoadAction, postLoadActionShouldWaitForIframeToLoad, postLoadSelector, postLoadPage })
    }

    if (target.includes('OPEN TIME DRAWER')) {
      setTimeRecordingDrawerIsOpen(true)
      await setTimeRecordingFormToDisplay('createPosting', `${clientNo}-${matterNo}`)
      shouldNavigate = false
    }

    if (target.includes('NEWKEYDATE')) {
      toggleKeyDatesDrawer({ keyDatesDrawerIsOpen: true, keyDatesDrawerMode: 'create' })
      shouldNavigate = false
    }

    if (label.includes('Add matter')) {
      // pre-populate the client wizard
      // @ts-ignore
      const firstClient = rowData?.clientNames?.[0] || null
      let forename = ''
      let surname = ''
      let emailAddress = ''
      let clientName = ''

      if (firstClient) {
        forename = firstClient.clientType === 'I' ? firstClient.forename : ''
        surname = firstClient.surname || ''
        // @ts-ignore
        emailAddress = rowData?.emailAddress || ''
        clientName = [forename, surname].filter(Boolean).join(' ')
      }

      useNewClientStore.setState({
        isNew: false,
        existingClient: { clientNo, forename, surname, emailAddress, clientName }
      })
    }

    if (target.includes('DOWNLOAD CSV')) {
      if (isClientsTable === true) {
        // Use clientsTableFilters from the store.
        if ((clientsTableFilters.totalRowCount || 0) > 10000) {
          alert(
            `You have selected ${clientsTableFilters.totalRowCount || 0} records, but this feature is limited to 10000 records. Please refine your search to include fewer results, or contact support@ospreyapproach.com if you require further assistance`
          )
          return
        }

        fetchClientTableAsCSV({
          // Pass the clients state (from the store) here.
          clients: clientsTableFilters
        })

        const blob = new Blob([clientTableDataCSV], { type: 'text/csv' })
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = 'client_export.csv'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(blobUrl)
      } else {
        // Use mattersTableFilters from the store.
        if ((mattersTableFilters.totalRowCount || 0) > 10000) {
          alert(
            `You have selected ${mattersTableFilters.totalRowCount || 0} records, but this feature is limited to 10000 records. Please refine your search to include fewer results, or contact support@ospreyapproach.com if you require further assistance`
          )
          return
        }

        fetchMatterTableAsCSV({
          matters: mattersTableFilters
        })

        const blob = new Blob([matterTableDataCSV], { type: 'text/csv' })
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = 'matter_export.csv'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(blobUrl)
      }

      shouldNavigate = false
    }

    if (shouldNavigate) {
      navigateWithClientMatterNo({
        route: target,
        clientMatterNo: `${clientNo}-${matterNo}`,
        appendClientMatterNo: appendClientMatterNoToTarget
      })
    }

    toggleMenu(false)
  }

  return (
    <MUIStack>
      <IconButton onClick={() => toggleMenu(true)} ref={ref} disabled={isDisabled}>
        <MoreVertIcon sx={{ height: '20px', width: '20px', opacity: isDisabled ? 0.3 : 1 }} />
      </IconButton>

      <PopupWithBackdrop menuState={menuState} anchorRef={ref} toggleMenu={toggleMenu} className="masterActionMenu">
        {menuItems &&
          menuItems.map(
            ({
              label,
              icon,
              target,
              appendClientMatterNoToTarget,
              children,
              requiresCurrentMatter,
              requiredMenuItemId,
              postLoadAction,
              postLoadSelector,
              divider
            }: any) => {
              if (divider) return <MenuDivider key={label} />

              if (children) {
                return (
                  <SubMenu
                    key={label}
                    label={<NavigationMenuItem label={label} icon={icon} isSubMenu={true} sx={{ cursor: 'default' }} />}
                  >
                    {children.map(
                      ({
                        label,
                        target,
                        appendClientMatterNoToTarget,
                        postLoadAction,
                        postLoadSelector,
                        requiresCurrentMatter,
                        requiredMenuItemId
                      }: any) => {
                        return (
                          <MenuItem
                            key={label}
                            onClick={() =>
                              handleMenuItemClick(
                                target,
                                label,
                                postLoadAction,
                                postLoadSelector,
                                appendClientMatterNoToTarget
                              )
                            }
                            disabled={
                              (requiresCurrentMatter && !currentClientMatterNo) ||
                              (requiredMenuItemId && !allowedMenuIds.includes(requiredMenuItemId)) ||
                              (label === 'Archive matter' && matterHasOutstandingUndertakings) ||
                              (label === 'Restore matter' && matterHasOutstandingUndertakings)
                            }
                          >
                            {label}
                          </MenuItem>
                        )
                      }
                    )}
                  </SubMenu>
                )
              }

              return (
                <MenuItem
                  key={label}
                  onClick={() =>
                    handleMenuItemClick(target, label, postLoadAction, postLoadSelector, appendClientMatterNoToTarget)
                  }
                  disabled={
                    (requiresCurrentMatter && !currentClientMatterNo) ||
                    (requiredMenuItemId && !allowedMenuIds.includes(requiredMenuItemId)) ||
                    (label === 'Archive matter' && matterHasOutstandingUndertakings) ||
                    (label === 'Restore matter' && matterHasOutstandingUndertakings)
                  }
                >
                  {icon || null}
                  {label}
                </MenuItem>
              )
            }
          )}
      </PopupWithBackdrop>
    </MUIStack>
  )
}

type MoreMenuCardProps = {
  clientNo?: string
  matterNo?: number
  rowData?: any
  isClientsTable?: boolean
  disabled?: boolean
}
